import { Component, OnInit, signal, inject, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { confirmPassword } from 'src/app/shared/validators/confirm-password-validator';
import { FormChecker } from 'src/app/shared/helpers/check-form';
import { AuthService } from '../../services/auth.service';
import { SystemStatusControllerService } from 'src/app/api-client';
import { PublicV2Service } from 'src/app/core/api-v2/public-v2.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import {
  NUMBERS_ONLY,
  PHONE_PATTERN,
} from 'src/app/shared/validation-patterns/validation-patterns-list';

/** Manual company-data controls revealed when the FINA registry is unavailable */
const FINA_FALLBACK_CONTROLS = [
  'companyName',
  'address1',
  'city',
  'postalCode',
] as const;

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements OnInit {
  public _auth = inject(AuthService);

  private _systemStatus = inject(SystemStatusControllerService);

  private _publicV2 = inject(PublicV2Service);

  private _notification = inject(NotificationService);

  private _transloco = inject(TranslocoService);

  private router = inject(Router);

  public passwordShown = signal(false);

  public passwordRetypedShown = signal(false);

  /** FINA registry unavailable — manual company-data fallback is active */
  public finaDown = signal(false);

  /** Registrations are reviewed manually — nudge toward an official company e-mail */
  public manualReview = signal(false);

  public companyForm: FormGroup;

  public authForm: FormGroup;

  public desktopMode: boolean = false;

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.formInitialization();
    this.resize();
    localStorage.getItem('FIRST_TIME_ON_PERSONALIZATION') &&
      localStorage.removeItem('FIRST_TIME_ON_PERSONALIZATION');
    this.checkFinaAvailability();
    this.checkRegistrationMode();
  }

  /** When registrations are reviewed manually (auto-approve off), show the
   * official-company-e-mail nudge — an official domain speeds up approval. */
  private checkRegistrationMode() {
    this._publicV2.getRegistrationConfig().subscribe({
      next: (config) => this.manualReview.set(config?.autoApprove === false),
      // Config unavailable — rather than guess, just skip the nudge.
      error: () => {},
    });
  }

  /** Initialization of the step forms */
  private formInitialization() {
    this.authForm = this.fb.group(
      {
        email: [null, [Validators.required, Validators.email]],
        password: [null, [Validators.required, Validators.minLength(5)]],
        passwordRetyped: [null, [Validators.required]],
      },
      {
        validators: confirmPassword,
      }
    );

    this.companyForm = this.fb.group({
      companyNo: [
        null,
        [Validators.required, Validators.pattern(NUMBERS_ONLY)],
      ],
      phone1: [null, [Validators.required, Validators.pattern(PHONE_PATTERN)]],
      checked: [null, Validators.requiredTrue],
    });
  }

  /** Query the backend for FINA registry availability and toggle the fallback */
  private checkFinaAvailability() {
    this._systemStatus.getFinaAvailability().subscribe({
      next: (status) =>
        this.setFinaDown((status as any)?.available === false),
      // Availability check failed — keep the regular flow; the pre-submit
      // re-check will reveal the fallback if FINA is really down.
      error: () => {},
    });
  }

  /** Reveal/hide the manual company-data fields (FINA-down fallback)
   * @param down true when the FINA registry is unavailable
   */
  private setFinaDown(down: boolean) {
    if (down === this.finaDown()) return;

    if (down) {
      FINA_FALLBACK_CONTROLS.forEach((name) =>
        this.companyForm.addControl(
          name,
          this.fb.control(null, Validators.required)
        )
      );
    } else {
      FINA_FALLBACK_CONTROLS.forEach((name) =>
        this.companyForm.removeControl(name)
      );
    }
    this.finaDown.set(down);
  }

  /** Register user */
  public register() {
    FormChecker.checkForm(this.companyForm);

    FormChecker.checkForm(this.authForm);

    if (this.companyForm.invalid || this.authForm.invalid) {
      return;
    }

    if (this.finaDown()) {
      this.registerWithManualData();
      return;
    }

    // v1 flow. If the POST fails because FINA went down between the initial
    // availability check and this submit, re-check and reveal the manual
    // fields so the user can complete registration.
    this._auth.register(this.companyForm, this.authForm, () =>
      this.recheckFinaAvailability()
    );
  }

  /** v1 registration request failed — the FINA registry may have gone down.
   * Re-check availability and, when unavailable, reveal the manual fields. */
  private recheckFinaAvailability() {
    this._systemStatus.getFinaAvailability().subscribe({
      next: (status) => {
        if ((status as any)?.available === false) {
          this.revealManualFields();
        }
      },
      error: () => {},
    });
  }

  /** Submit the v2 registration carrying the manually entered company data */
  private registerWithManualData() {
    this._auth.loading = true;
    this._publicV2
      .registerCompany({
        email: this.authForm.value.email,
        password: this.authForm.value.password,
        phone1: this.companyForm.value.phone1,
        companyNo: this.companyForm.value.companyNo,
        companyName: this.companyForm.value.companyName,
        address1: this.companyForm.value.address1,
        city: this.companyForm.value.city,
        postalCode: this.companyForm.value.postalCode,
      })
      .subscribe({
        next: () => {
          this._auth.loading = false;
          const email = this.authForm.value.email;
          this._notification.success(
            this._transloco.translate('login.verificationCodeSent', { email })
          );
          // Same code-entry flow as the v1 path: carry the e-mail, and after
          // verifying send the user to auth/login (afterVerify: 'login').
          this.router.navigate(['/verify-email'], {
            state: { email, afterVerify: 'login' },
          });
        },
        // Validation errors (e.g. error.finaUnavailable.manualDataRequired)
        // are surfaced by the global ErrorService; keep the manual fields
        // visible (they already are) without stacking another toast.
        error: () => {
          this._auth.loading = false;
        },
      });
  }

  /** Show the FINA-down note + manual fields and let the user complete them */
  private revealManualFields() {
    this.setFinaDown(true);
    this._notification.error(
      this._transloco.translate('login.finaDownTitle')
    );
  }

  @HostListener('window:resize') resize() {
    if (window.innerWidth < 993) {
      this.desktopMode = false;
    } else this.desktopMode = true;
  }
}
